import type { Viewport } from "next"

/**
 * The colour a route paints on `html` / `body`.
 *
 * Why this exists: iOS 26 Safari tints its (always translucent) status bar
 * and toolbar from the page's *root* background — the `background-color` of
 * `html`/`body`, or of a fixed/sticky element that hugs the viewport edge.
 * It ignores `<meta name="theme-color">` entirely. So a dark page that only
 * paints a dark `<div>` over the site-wide light canvas (globals.css sets
 * `html, body { background: var(--background) }`, light unless `html.dark`)
 * shows a WHITE box top and bottom on iPhone. Nothing in the page is white;
 * Safari is tinting its bars from the light root underneath.
 *
 * The rule (also in RULES.md): a page's colour lives on `html`/`body`, server
 * rendered. Render `<PageCanvas>` once from the route's layout (or page) so
 * first paint is already right, and pair it with `canvasViewport()` for
 * Android Chrome, which still reads theme-color. Don't make the root
 * transparent (Safari falls back to its own white chrome) and don't rely on
 * a z-index:-1 fixed backdrop (Safari hit-tests the content above it).
 */
export function PageCanvas({ color, scheme }: { color: string; scheme?: "dark" | "light" }) {
  return (
    <style>{`html, body { background-color: ${color} !important;${scheme ? ` color-scheme: ${scheme};` : ""} }`}</style>
  )
}

/** The Android half: `export const viewport = canvasViewport(COLOR)` next to the layout. */
export function canvasViewport(color: string): Viewport {
  return { themeColor: color }
}

/**
 * Runtime half for surfaces whose colour changes after hydration (the glass
 * pages' light/dark toggle). Keeps `html`/`body` in step so Safari's bars
 * follow the toggle; the layout's `<PageCanvas>` covers first paint.
 */
export function setPageCanvas(color: string) {
  if (typeof document === "undefined") return
  for (const el of [document.documentElement, document.body]) {
    el.style.setProperty("background-color", color, "important")
  }
}
